import { BillOfLading } from '@/models/BillOfLading'
import { UserPageView } from '@/models/UserPageView'
import { Document } from '@/models/Document'
import { Permission } from '@/models/Permission'
import Router from 'vue-router'
import User from '@/models/User'
import Vue from 'vue'
import { store } from '@/store'
import { ensureValidSession } from '@/auth/session'
import { getPreferredLegacyRedirect } from '@/utils/viewVersionSwitcher'

Vue.use(Router)

// a function to retry loading a chunk to avoid chunk load error for out of date code
const lazyRetry = function (componentImport) {
	return new Promise((resolve, reject) => {
		// check if the window has already been refreshed
		const hasRefreshed = JSON.parse(window.sessionStorage.getItem('retry-lazy-refreshed') ?? 'false')
		// try to import the component
		componentImport()
			.then((component) => {
				window.sessionStorage.setItem('retry-lazy-refreshed', 'false') // success so reset the refresh
				resolve(component)
			})
			.catch((error) => {
				if (!hasRefreshed) {
					// not been refreshed yet
					window.sessionStorage.setItem('retry-lazy-refreshed', 'true') // we are now going to refresh
					return window.location.reload() // refresh the page
				}
				reject(error) // Default error behavior as already tried refresh
			})
	})
}

const pushUserDataToGoogleTagManager = (user): void => {
	if ((window as any).dataLayer.find((data) => data.logged_in_id) === undefined) {
		;(window as any).dataLayer.push({
			logged_in_id: user.getApiId(),
			logged_in_name: [user.firstName, user.lastName].join(' '),
			email: user.email,
			company_str: user.companyName,
			segment: user.role
		})
	}
}

const router = new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	routes: [
		{
			path: '/',
			name: 'home',
			redirect: () => {
				return { name: 'welcome' }
			}
		},
		{
			path: '/welcome',
			name: 'welcome',
			component: () => lazyRetry(() => import(/* webpackChunkName: "welcome" */ '@/views/Welcome.vue'))
		},
		{
			path: '/map/:category?',
			name: 'map',
			component: () => lazyRetry(() => import(/* webpackChunkName: "map" */ '@/views/Map.vue')),
			meta: {
				auth: true,
				permission: 'maps.index'
			},
			children: [
				{
					path: '/map/:category/:id',
					name: 'map.facility',
					props: true,
					component: () => lazyRetry(() => import(/* webpackChunkName: "map" */ '@/views/Map/Facility.vue'))
				},
				{
					path: '/map/directions',
					name: 'map.directions',
					props: true,
					component: () => lazyRetry(() => import(/* webpackChunkName: "map" */ '@/views/Map/Directions.vue'))
				}
			]
		},
		{
			path: '/cargo',
			component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Cargos.vue')),
			redirect: 'cargo',
			meta: {
				auth: true,
				permission: 'offers.index'
			},
			children: [
				{
					path: '',
					name: 'cargo',
					component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Index.vue'))
				},
				{
					name: 'cargo.owned',
					path: 'owned',
					redirect: { name: 'cargo.owned.index' },
					components: {
						default: () =>
							lazyRetry(
								() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/OwnedPlaceholder.vue')
							),
						toolbar: () =>
							lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/UserFilter.vue'))
					},
					children: [
						{
							path: '',
							name: 'cargo.owned.index',
							components: {
								default: () =>
									lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Owned.vue')),
								toolbar: () =>
									lazyRetry(
										() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/UserFilter.vue')
									)
							}
						},
						{
							name: 'cargo.owned.scheduled',
							path: 'scheduled',
							components: {
								default: () =>
									lazyRetry(
										() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Scheduled.vue')
									),
								toolbar: () =>
									lazyRetry(
										() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/UserFilter.vue')
									)
							}
						}
					]
				},
				{
					name: 'cargo.projects',
					path: 'projects',
					components: {
						default: () =>
							lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Projects.vue')),
						toolbar: () =>
							lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/UserFilter.vue'))
					}
				},
				{
					name: 'cargo.external',
					path: 'external',
					component: () =>
						lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/External.vue'))
				}
			]
		},
		{
			path: '/cargo/project/options',
			name: 'cargo.project.options',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/CargoProject/CargoProjectOptions.vue')),
			meta: {
				auth: true,
				permission: 'cargoProjects.store'
			}
		},
		{
			path: '/cargo/project/:cargoProjectId/edit',
			name: 'cargo.project.edit',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/CargoProject/CargoProjectEdit.vue')),
			meta: {
				auth: true,
				permission: 'cargoProjects.update'
			}
		},
		{
			path: '/cargo/project/:cargoProjectId/cargo-blueprint',
			name: 'cargo.project.cargo-blueprint',
			props: true,
			component: () =>
				lazyRetry(
					() => import(/* webpackChunkName: "cargo" */ '@/views/CargoProject/CargoProjectBlueprint.vue')
				),
			meta: {
				auth: true,
				permission: 'cargoProjects.store'
			}
		},
		{
			path: '/cargo/project/:cargoProjectId',
			name: 'cargo.project.show',
			props: true,
			component: () =>
				lazyRetry(
					() => import(/* webpackChunkName: "cargo" */ '@/views/CargoProject/ViewCargoProjectDetails.vue')
				),
			meta: {
				auth: true,
				permission: 'cargoProjects.show'
			}
		},
		{
			path: '/cargo/:cargoId/edit',
			name: 'cargo.edit',
			props: true,
			component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/EditCargo.vue')),
			meta: {
				auth: true,
				permission: 'cargos.update'
			}
		},

		{
			path: '/cargo/offers',
			name: 'cargo.offers.index',
			component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/CargoOffers.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				permission: 'offers.index'
			}
		},
		{
			path: '/cargo/offers/:cargoId',
			name: 'cargo.offers.show',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/ViewCargoDetails.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				permission: 'offers.show'
			}
		},
		{
			path: '/cargo/offers/:cargoId/offer-details',
			name: 'cargo.offers.details',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/ViewOfferDetails.vue')),
			meta: {
				auth: true,
				permission: 'cargos.show'
			}
		},
		{
			path: '/cargo/offers/:cargoId/empty-vessels',
			name: 'cargo.empty-vessels.index',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/ViewEmptyVessels.vue')),
			meta: {
				auth: true,
				permission: 'empties.index'
			}
		},
		{
			path: '/cargo/offers/:cargoId/replies/:replyType/:replyId',
			name: 'cargo.reply.show',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/ViewOfferReply.vue')),
			meta: {
				auth: true,
				permission: 'cargos.store'
			}
		},
		{
			path: '/cargo/offers/:cargoId/replies/:replyType/:replyId/preview-charter',
			name: 'cargo.reply.preview-charter',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/PreviewCharter.vue')),
			meta: {
				auth: true,
				permission: 'charter.store'
			}
		},
		{
			path: '/cargo/offers/:cargoId/replies/charters/:charterId/accept-charter',
			name: 'cargo.offers.accept-charter',
			props: true,
			component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/AcceptCharter.vue')),
			meta: {
				auth: true,
				permission: 'charter.update'
			}
		},

		{
			path: '/cargo/offers/:cargoId/replies/:replyType/:replyId/edit-charter',
			name: 'cargo.reply.edit-charter',
			props: true,
			component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/EditCharter.vue')),
			meta: {
				auth: true,
				permission: 'cargos.update'
			}
		},
		{
			path: '/cargo/offers/:cargoId/preview-quotation',
			name: 'cargo.preview-quotation',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/PreviewQuotation.vue')),
			meta: {
				auth: true,
				permission: 'charter.store'
			}
		},
		{
			path: '/cargo/options',
			name: 'cargo.options',
			component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/CargoOptions.vue')),
			props: true,
			meta: {
				auth: true
			}
		},
		{
			path: '/cargo/:cargoId/route',
			name: 'cargo.route.index',
			props: true,
			component: () => lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/RouteCompare.vue')),
			meta: {
				auth: true,
				permission: 'offers.show'
			}
		},
		{
			path: '/cargo/:cargoId/publish',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/CargoPublication.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				permission: 'offers.store'
			},
			children: [
				{
					path: '',
					name: 'cargo.route.publish.index',
					component: () =>
						lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Publication/Index.vue'))
				},
				{
					name: 'cargo.route.publish.tender',
					path: 'tender',
					component: () =>
						lazyRetry(() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Publication/Tender.vue'))
				},
				{
					name: 'cargo.route.publish.schedule',
					path: 'schedule',
					component: () =>
						lazyRetry(
							() => import(/* webpackChunkName: "cargo" */ '@/views/Cargo/Publication/Schedule.vue')
						)
				}
			]
		},
		{
			path: '/messages',
			name: 'messages',
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/messages'
			}
		},
		{
			path: '/transports',
			name: 'transports',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "transports" */ '@/views/Transport/Transports.vue')),
			props: true,
			meta: {
				auth: true,
				permission: 'transports.index'
			}
		},
		{
			path: '/transports/:transportId',
			name: 'transports.show',
			props: true,
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "transports" */ '@/views/Transport/TransportDetail.vue')),
			meta: {
				auth: true,
				permission: 'transports.show',
				fetchResources: [new Document().getJsonApiType()]
			}
		},
		{
			path: '/gas-oil-usage/create-item',
			name: 'gas-oil-usage.create',
			component: () =>
				lazyRetry(
					() => import(/* webpackChunkName: "gasOilUsage" */ '@/views/Transport/CreateGasOilUsage.vue')
				),
			meta: {
				auth: true
				// permission: 'vessel-logs.store',
			}
		},
		{
			path: '/gas-oil-usage/edit-item/:gasOilUsageId',
			name: 'gas-oil-usage.edit',
			props: true,
			component: () =>
				lazyRetry(
					() => import(/* webpackChunkName: "gasOilUsage" */ '@/views/Transport/CreateGasOilUsage.vue')
				),
			meta: {
				auth: true
				// permission: 'vessel-logs.update',
			}
		},
		{
			path: '/insight',
			name: 'dashboard-selection',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "dashboard" */ '@/views/DashboardSelection.vue')),
			meta: {
				auth: true,
				v3Route: '/insight'
			}
		},
		{
			path: '/insight/dashboard',
			component: () => lazyRetry(() => import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard.vue')),
			props: true,
			meta: {
				auth: true
			},
			children: [
				{
					path: '',
					name: 'dashboard.index',
					component: () =>
						lazyRetry(() => import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard/Index.vue')),
					meta: {
						permission: 'insights.index',
						additional_details_required: true
					}
				},
				{
					name: 'dashboard.insights',
					path: 'insights',
					components: {
						default: () =>
							lazyRetry(
								() => import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard/Insights.vue')
							),
						header: () =>
							lazyRetry(
								() =>
									import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard/DashboardFilter.vue')
							)
					}
				}
			]
		},
		{
			path: '/insight/dashboard-transport',
			name: 'dashboard-transport',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "dashboard-transport" */ '@/views/TransportDashboard.vue')),
			meta: {
				auth: true
			}
		},
		{
			path: '/insight/inventory-management',
			name: 'inventory-management',
			component: () =>
				lazyRetry(
					() => import(/* webpackChunkName: "inventory-management" */ '@/views/InventoryManagement.vue')
				),
			meta: {
				auth: true,
				v3Route: '/inventory-management'
			}
		},
		{
			path: '/insight/port-management',

			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "port-management" */ '@/views/PortManagement.vue')),
			meta: {
				auth: true,
				permission: 'insights.index',
				additional_details_required: true
			},
			children: [
				{
					path: '',
					name: 'port-management',
					component: () =>
						lazyRetry(() => import(/* webpackChunkName: "port-management" */ '@/views/PortDashboard.vue')),
					meta: {
						auth: true,
						permission: 'insights.index',
						additional_details_required: true
					}
				},
				{
					path: '/port-report',
					name: 'port-management.report',
					component: () =>
						lazyRetry(() => import(/* webpackChunkName: "port-management" */ '@/views/PortReport.vue')),
					meta: {
						auth: true,
						permission: 'insights.index',
						additional_details_required: true,
						v3Route: '/port-report'
					}
				}
			]
		},
		{
			path: '/loading-bill',
			name: 'loading-bill',
			component: () => lazyRetry(() => import(/* webpackChunkName: "loading-bill" */ '@/views/LoadingBill.vue')),
			meta: {
				auth: true,
				v3Route: '/loading-bill'
			}
		},
		{
			path: '/invoicing',
			name: 'invoicing',
			component: () => lazyRetry(() => import(/* webpackChunkName: "invoicing-v3" */ '@/views/InvoicingV3.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/invoicing'
			}
		},
		{
			path: '/terminals',
			name: 'terminals',
			component: () => lazyRetry(() => import(/* webpackChunkName: "terminals" */ '@/views/Terminals.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/terminals'
			}
		},
		{
			path: '/depot',
			name: 'depot',
			component: () => lazyRetry(() => import(/* webpackChunkName: "depot" */ '@/views/Depot.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/depots'
			}
		},
		{
			path: '/capacity-radar',
			name: 'capacity-radar',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "capacity-radar" */ '@/views/CapacityRadar.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/capacity-radar'
			}
		},
		{
			path: '/cargo-radar',
			name: 'cargo-radar',
			component: () => lazyRetry(() => import(/* webpackChunkName: "capacity-radar" */ '@/views/CargoRadar.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/cargo-radar'
			}
		},
		{
			path: '/ai-chat',
			name: 'ai-chat',
			component: () => lazyRetry(() => import(/* webpackChunkName: "ai-chat" */ '@/views/AiChat.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/demo/ai-chat'
			}
		},
		{
			path: '/relation-management',
			name: 'relation-management',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "relation-management" */ '@/views/CompanyRelation.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/relation-management'
			}
		},
		{
			path: '/profile',
			name: 'profile',
			component: () => lazyRetry(() => import(/* webpackChunkName: "profile" */ '@/views/Profile.vue')),
			redirect: { name: 'profile.user.index' },
			meta: {
				auth: true
				// additional_details_required: true
			},
			children: [
				{
					path: 'user',
					name: 'profile.user',
					component: () =>
						lazyRetry(() => import(/* webpackChunkName: "profile" */ '@/views/Profile/User.vue')),
					meta: {
						auth: true,
						v3Route: '/profile/user'
					}
				},
				{
					path: 'company-management',
					name: 'profile.company-management',
					component: () =>
						lazyRetry(
							() =>
								import(
									/* webpackChunkName: "company-management" */ '@/views/Profile/CompanyManagement.vue'
								)
						),
					meta: {
						auth: true,
						// additional_details_required: true,
						v3Route: '/profile/company'
					}
				},
				{
					path: 'vessel',
					name: 'profile.vessel',
					redirect: { name: 'profile.vessel.management' },
					meta: {
						auth: true
						// additional_details_required: true
					},
					children: [
						{
							path: '',
							name: 'profile.vessel.management',
							component: () =>
								lazyRetry(
									() =>
										import(/* webpackChunkName: "profile" */ '@/views/Profile/VesselManagement.vue')
								),
							meta: {
								auth: true,
								additional_details_required: true,
								v3Route: '/profile/vessel/management'
							}
						},
						{
							path: 'documents',
							name: 'profile.vessel.documents',
							component: () =>
								lazyRetry(
									() =>
										import(
											/* webpackChunkName: "profile" */ '@/views/Preferences/VesselDocuments.vue'
										)
								)
						}
					]
				}
			]
		},
		{
			path: '/profile/callback',
			name: 'profile.callback',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "profile" */ '@/views/ConnectionCallback.vue')),
			meta: {
				auth: true,
				additional_details_required: true
			}
		},
		{
			path: '/cockpit',
			name: 'cockpit',
			component: () => lazyRetry(() => import(/* webpackChunkName: "cockpit" */ '@/views/Cockpit.vue')),
			meta: {
				auth: true,
				additional_details_required: true,
				v3Route: '/cockpit'
			}
		},
		{
			path: '/additional-information',
			name: 'additional-information',
			component: () =>
				lazyRetry(() => import(/* webpackChunkName: "additional-form" */ '@/views/AdditionalForm.vue')),
			meta: {
				auth: true,
				v3Route: '/complete-registration'
			}
		},
		{
			path: '/login',
			name: 'login',
			component: () => lazyRetry(() => import(/* webpackChunkName: "login" */ '@/views/Auth/Login.vue'))
		},
		{
			path: '/register',
			name: 'register',
			component: () => lazyRetry(() => import(/* webpackChunkName: "guest" */ '@/views/Auth/Register.vue'))
		},
		{
			path: '/forbidden',
			name: 'forbidden',
			component: () => lazyRetry(() => import('@/views/Forbidden.vue'))
		},
		{
			path: '/not-found',
			name: 'notfound',
			component: () => lazyRetry(() => import('@/views/NotFound.vue'))
		},
		{
			path: '/active-pending',
			name: 'active-pending',
			component: () => lazyRetry(() => import('@/views/ActivePending.vue'))
		},
		{
			path: '/track-and-trace/:trackAndTraceId',
			name: 'trackandtrace',
			component: () => lazyRetry(() => import('@/views/TrackAndTrace.vue'))
		},
		{
			path: '/track-and-trace',
			name: 'create-trackandtrace',
			component: () => lazyRetry(() => import('@/views/TrackAndTrace.vue'))
		}
	]
})

const getUser = async (): Promise<object | null> => {
	try {
		await ensureValidSession()
		const shouldForceSync = !store.getters['auth/isInitialized']

		await Promise.all([Permission.sync(), User.sync(shouldForceSync)])

		const user = await User.getCurrentUser()
		pushUserDataToGoogleTagManager(user)
		// @ts-ignore
		window.NREUM?.setUserId(user.getApiId())
	} catch (e) {
		if (e !== 'The user is not authenticated') {
			// eslint-disable-next-line
			console.error(e)
		}
	}

	return null
}

router.beforeResolve(async (to, from, next) => {
	if (localStorage.getItem('forceLogin')) {
		// "Sync" the permissions and user so that we may intercept them during testing
		await Promise.all([Permission.sync(), User.sync()])
	}

	if (!localStorage.getItem('forceLogin') && to.matched.some((record) => record.meta.auth)) {
		await getUser()
	}

	if (to.matched.some((record) => record.meta.additional_details_required)) {
		// @ts-ignore
		if (!store.state.auth.user.active) {
			await User.sync(true) // sync the user one last time to know for sure

			// @ts-ignore
			if (!store.state.auth.user.active) {
				return next({
					name: store.getters['auth/isActivePending'] ? 'active-pending' : 'additional-information'
				})
			}
		}

		if (store.getters['auth/userShouldRegisterVessel']) {
			return next({
				name: 'additional-information',
				query: { url: to.fullPath }
			})
		}
	}

	if (
		to.matched.some((route) => {
			return route.meta.permission && !store.getters['auth/hasPermission'](route.meta.permission)
		})
	) {
		return next({ name: 'forbidden' })
	}

	if (to.matched.some((record) => record.meta.v3Route)) {
		// Check if user prefers the legacy view for this route
		const legacyRedirect = getPreferredLegacyRedirect(to.name as string)
		if (legacyRedirect) {
			return next({ name: legacyRedirect })
		}

		// @ts-ignore
		store.commit('v3/setCurrentPage', to.matched!.find((record) => record.meta!.v3Route!)!.meta.v3Route)
		store.commit('v3/setInitialized', true)
	} else {
		store.commit('v3/setCurrentPage', null)
	}

	return next()
})

router.afterEach((to) => {
	if (!to.matched.some((r) => r.meta.auth)) return
	const slug = to.name as string
	if (!slug) return
	const pageView = new UserPageView()
	pageView.page = slug
	pageView.save().catch(() => {})
})

export default router
